// React
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Date Time
import moment from "moment";
import { DateTime } from "luxon";

// Components
import {
  Card,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  AlertTitle,
  Box,
  Stack,
  Link,
} from "@mui/material";
import { Person, AccessTime, CalendarToday } from "@mui/icons-material";
import LockedCardsDialog from "./LockedCardsDialog";
import {
  cancelAppointment,
  sendCancellation,
} from "../../api/AppointmentEndpointRequests";
import CustomButton from "../../constants/CustomButton";

// Icons
import {
  BorderColor,
  Delete,
  PendingActions,
  EventAvailable,
} from "@mui/icons-material";

// Constants
import { darkBlue } from "../../constants/ColourConstants";

// Styles
import { styles } from "./styles";
import { styles as mainStyles } from "../styles";
import ConfirmModal from "components/Common/ConfirmModal";
import FeedbackModal from "components/Common/FeedbackModal";
import handleCancelWaitlist from "components/Waitlist/handleCancelWaitlist";

const MANITOBA_TIMEZONE = "America/Winnipeg";

const PatientCard = ({
  data,
  onDelete,
  todayAppointmentAlert,
  unpaidAppointmentsAlert,
  lockedCards,
  unpaidAppointments,
  paidAppointments,
  waitlistData,
  activeWaitListStatus,
}) => {
  const navigate = useNavigate();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const today = moment().format("YYYY-MM-DD");
  const updatedData = {
    ...data,
    unpaidAppointments: unpaidAppointments,
    paidAppointments: paidAppointments,
  };
  const patientName = `${updatedData?.demographics?.firstName} ${updatedData?.demographics?.lastName}`;
  const navigateTo = (page) => {
    navigate(page, { state: updatedData });
  };

  const NameDisplay = ({ firstName = "", lastName = "" }) => {
    const fullName = `${firstName} ${lastName}`.trim();
    const maxLen = 20;
    const isLong = fullName.length > maxLen;
    const display = isLong ? `${fullName.slice(0, maxLen - 1)}…` : fullName;

    const nameNode = (
      <Typography color={darkBlue} sx={styles.cardFullName}>
        {display}
      </Typography>
    );

    return isLong ? (
      <Tooltip enterTouchDelay={50} title={fullName} arrow>
        {nameNode}
      </Tooltip>
    ) : (
      nameNode
    );
  };

  const appointment = updatedData?.patientAppointments[0];

  const hasTodayAppointment = todayAppointmentAlert.includes(
    updatedData?.patientId
  );
  const hasUnpaidAppointment = unpaidAppointmentsAlert.includes(
    updatedData?.patientId
  );
  const onWaitlist = activeWaitListStatus.includes(updatedData?.patientId);

  const apptTimePassedCheck = (appointment) => {
    const apptTime = DateTime.fromFormat(
      `${appointment.date} ${appointment.startTime}`,
      "yyyy-MM-dd Hmm"
    ).setZone(MANITOBA_TIMEZONE, { keepLocalTime: true });
    const currentTime = DateTime.now().setZone(MANITOBA_TIMEZONE);
    return apptTime < currentTime;
  };

  const apptCancellationTimePassedCheck = (appointment) => {
    const apptTime = DateTime.fromFormat(
      `${appointment.date} ${appointment.startTime}`,
      "yyyy-MM-dd Hmm"
    ).setZone(MANITOBA_TIMEZONE, { keepLocalTime: true });
    const cutOffTime = apptTime.minus({ hours: 2 });
    const currentTime = DateTime.now().setZone(MANITOBA_TIMEZONE);
    return cutOffTime < currentTime;
  };

  const formatPhin = (phn) => {
    if (!phn) return null;
    const digits = String(phn).replace(/\D/g, "");
    if (digits.length !== 9) return phn;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };

  const getApptCancelData = () => ({
    title: "CONFIRM CANCELLATION",
    message: (
      <Stack>
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row">
            <Person sx={mainStyles.icon} />
            <Typography>{patientName}</Typography>
          </Stack>
          <Stack direction="row">
            <CalendarToday sx={mainStyles.icon} />
            <Typography>{updatedData.upcomingAppt?.date}</Typography>
          </Stack>
          <Stack direction="row">
            <AccessTime sx={mainStyles.icon} />
            <Typography>{updatedData.upcomingAppt?.time}</Typography>
          </Stack>
        </Stack>

        <Typography sx={mainStyles.leftAlignedTypographyBody}>
          Are you sure you want to <strong>cancel</strong> this appointment?
        </Typography>
      </Stack>
    ),
    onConfirm: () => {
      handleCancelAppointment();
    },
    confirmText: "Cancel Appointment",
  });

  const getWaitlistCancelData = () => ({
    title: "CONFIRM CANCELLATION",
    message: (
      <Stack>
        <Stack direction="column" spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row">
            <Person sx={mainStyles.icon} />
            <Typography>{patientName}</Typography>
          </Stack>
          <Stack direction="row">
            <CalendarToday sx={mainStyles.icon} />
            <Typography>
              {DateTime.fromISO(today).toFormat("cccc, LLLL dd yyyy")}
            </Typography>
          </Stack>
        </Stack>

        <Typography sx={mainStyles.leftAlignedTypographyBody}>
          Are you sure you want to remove yourself from the waitlist?
        </Typography>
      </Stack>
    ),
    onConfirm: () => {
      handleWaitlistCancel();
    },
    confirmText: "Cancel Waitlist",
  });

  const handleWaitlistCancel = async () => {
    setLoadingAction(true);
    const selectedWaitlistPatientId = waitlistData.filter(
      (waitlist) => waitlist.patientId === updatedData?.patientId
    );
    if (selectedWaitlistPatientId && selectedWaitlistPatientId[0]) {
      const response = await handleCancelWaitlist(
        updatedData?.patientId,
        selectedWaitlistPatientId[0].id,
        selectedWaitlistPatientId[0].referralDate,
        selectedWaitlistPatientId[0].requestDate,
        today
      );
      if (response.error || response.message) {
        setFeedbackData({
          success: false,
          title: "Waitlist Cancellation Failed",
          message:
            "Something went wrong while cancelling the waitlist. Please try again.",
        });
      } else {
        setFeedbackData({
          success: true,
          title: "Waitlist Cancelled Successfully",
        });
      }
    }
    handleCloseConfirm();
    setLoadingAction(false);
    setOpenFeedback(true);
  };

  const handleCancelAppointment = async () => {
    setLoadingAction(true);

    if (apptCancellationTimePassedCheck(appointment)) {
      setFeedbackData({
        success: false,
        title: "Appointment Cancellation Failed",
        message:
          "Appointments can only be cancelled online up to 2 hours before the scheduled start time. Please contact the clinic directly for assistance.",
      });
    } else {
      const response = await cancelAppointment(appointment.appointmentId);
      if (response.error || response.message) {
        setFeedbackData({
          success: false,
          title: "Appointment Cancellation Failed",
          message:
            "Something went wrong while cancelling the appointment. Please try again.",
        });
      } else {
        setFeedbackData({
          success: true,
          title: "Appointment Cancelled Successfully",
        });
        const hasCellPhone = updatedData.demographics.phones.some(
          (item) => item.contactType === "CellPhone"
        );
        if (hasCellPhone) {
          const cellPhone = hasCellPhone
            ? `+1${updatedData.demographics.phones
                .filter((item) => item.contactType === "CellPhone")[0]
                ?.["number"].replace(/\D/g, "")}`
            : "";
          await sendCancelMessage(appointment, cellPhone);
        }
      }
    }
    handleCloseConfirm();
    setLoadingAction(false);
    setOpenFeedback(true);
  };

  const sendCancelMessage = async (appointment, number) => {
    const paddedStartTime = String(appointment.startTime).padStart(4, "0");
    const time = DateTime.fromFormat(paddedStartTime, "Hmm").toFormat("h:mm a");
    const body = {
      name: updatedData.demographics.firstName,
      date: appointment.date,
      time: time,
      cell: number,
    };
    await sendCancellation(body);
  };

  const handleOpenConfirm = (type) => {
    const data =
      type === "appointment" ? getApptCancelData() : getWaitlistCancelData();
    setConfirmData(data);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setConfirmData(null);
  };

  const handleCloseFeedback = () => {
    if (feedbackData.success) {
      window.location.reload();
    }
    setOpenFeedback(false);
    setFeedbackData(null);
  };

  return (
    <>
      {openConfirm && (
        <ConfirmModal
          open={openConfirm}
          title={confirmData.title}
          message={confirmData.message}
          onConfirm={confirmData.onConfirm}
          confirmText={confirmData.confirmText}
          loadingAction={loadingAction}
          onCancel={handleCloseConfirm}
        />
      )}
      {openFeedback && (
        <FeedbackModal
          open={openFeedback}
          success={feedbackData.success}
          title={feedbackData.title}
          message={feedbackData.message ?? ""}
          onConfirm={handleCloseFeedback}
        />
      )}
      <Card sx={styles.card}>
        {hasTodayAppointment && !apptTimePassedCheck(appointment) && (
          <Alert
            severity="success"
            sx={mainStyles.alertPatient}
            iconMapping={{ success: <EventAvailable /> }}
          >
            <Stack direction="row" justifyContent="space-between" pb={0.5}>
              <AlertTitle sx={{ mb: 0 }}>Upcoming Appointment</AlertTitle>
              <Button
                size="small"
                color="inherit"
                sx={{ p: 0 }}
                disabled={loadingAction}
                onClick={() => handleOpenConfirm("appointment")}
              >
                Cancel
              </Button>
            </Stack>
            <b>{updatedData.upcomingAppt?.date} </b>
            <br />
            <b>{updatedData.upcomingAppt?.time}</b>
          </Alert>
        )}

        {onWaitlist && (
          <Alert
            severity="info"
            sx={mainStyles.alertPatient}
            iconMapping={{ info: <PendingActions /> }}
          >
            <Stack direction="row" justifyContent="space-between" pb={0.5}>
              <AlertTitle sx={{ mb: 0 }}>Waitlist</AlertTitle>
              <Button
                size="small"
                color="inherit"
                sx={{ p: 0 }}
                disabled={loadingAction}
                onClick={() => handleOpenConfirm("waitlist")}
              >
                Cancel
              </Button>
            </Stack>
            The clinic will call you when a spot opens
          </Alert>
        )}

        {hasUnpaidAppointment && (
          <Alert severity="error" sx={mainStyles.alertPatient}>
            <AlertTitle sx={{ mb: 0, pb: 0.5 }}>
              No-show fee required
            </AlertTitle>
            Please call{" "}
            <Link
              href="tel:4312222273"
              underline="always"
              sx={{ fontWeight: 700, color: "inherit" }}
            >
              (431) 222-2273
            </Link>{" "}
            for missed appointment.
          </Alert>
        )}

        <Stack justifyContent="space-between" p={1} height="225px">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              height: "45px",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <NameDisplay
              firstName={updatedData?.demographics?.firstName}
              lastName={updatedData?.demographics?.lastName}
            />

            <Tooltip enterTouchDelay={50} title="Edit" arrow>
              <IconButton
                color="inherit"
                onClick={() => navigateTo("/editPatient")}
              >
                <BorderColor />
              </IconButton>
            </Tooltip>
            <Tooltip enterTouchDelay={50} title="Delete" arrow>
              <IconButton
                color="error"
                onClick={() =>
                  onDelete(
                    updatedData?.dynamoId,
                    hasTodayAppointment || hasUnpaidAppointment,
                    onWaitlist
                  )
                }
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={0.1} alignItems="flex-start">
            <Typography variant="body2">
              <b>DOB: </b>
              {moment(updatedData?.demographics?.birthday).format("ll")}
            </Typography>
            <Typography variant="body2">
              <b>PHIN: </b>
              {formatPhin(updatedData?.demographics?.healthCard.phn) ??
                "No Health Card"}
            </Typography>
            <Typography variant="body2">
              <b>Phone: </b>
              {updatedData?.demographics?.phones.filter(
                (item) => item.contactType === "CellPhone"
              )[0]?.number ??
                updatedData?.demographics?.phones.filter(
                  (item) => item.contactType === "HomePhone"
                )[0]?.number}
            </Typography>
          </Stack>

          <Stack direction="column" spacing={1} p={0}>
            {/* Left column */}
            <Box sx={{ flex: 1 }}>
              <CustomButton
                type="secondary"
                label="View Past Appointments"
                sx={{ width: "100%" }}
                onClick={() => navigateTo("/pastAppointments")}
              />
            </Box>

            {/* Right column */}
            <Box sx={{ flex: 1 }}>
              {hasUnpaidAppointment ? (
                <LockedCardsDialog />
              ) : hasTodayAppointment ? (
                <Tooltip
                  enterTouchDelay={50}
                  title="We currently allow one appointment per day"
                  arrow
                >
                  <CustomButton
                    type="colour"
                    sx={{ width: "100%" }}
                    label={
                      apptTimePassedCheck(appointment)
                        ? "Appointment Completed"
                        : "Appointment Booked"
                    }
                    color={
                      apptTimePassedCheck(appointment) ? "info" : "success"
                    }
                  />
                </Tooltip>
              ) : (
                <CustomButton
                  label=" Book New Appointment"
                  disabled={lockedCards}
                  sx={{ width: "100%" }}
                  onClick={() => navigateTo("/patientBooking")}
                />
              )}
            </Box>
          </Stack>
        </Stack>
      </Card>
    </>
  );
};

export default PatientCard;
